import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Helper to strip the account prefix (/u/:accountIndex) from pathname.
 * E.g. "/u/0/dashboard/profile" -> "/dashboard/profile"
 */
const normalizePath = (p) => (p || '').replace(/^\/u\/\d+/, '');

/**
 * ScrollToTop
 *
 * Ensures that whenever navigation occurs between pages in the application,
 * the scroll position resets to the top (0, 0) instead of retaining the previous
 * page's scroll position.
 *
 * Prevents scrolling to top when switching users while remaining on the same page
 * (e.g. /u/0/dashboard/profile -> /u/1/dashboard/profile).
 */
export default function ScrollToTop() {
  const { pathname, search, hash } = useLocation();
  const prevPathRef = useRef(pathname);

  useEffect(() => {
    // Disable browser's automatic scroll restoration on SPA navigation
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const prevPath = prevPathRef.current;
    prevPathRef.current = pathname;

    // If staying on the same page and only switching accounts, do NOT scroll to top
    if (prevPath && normalizePath(prevPath) === normalizePath(pathname)) {
      return;
    }

    const resetScroll = () => {
      // If a specific hash anchor is targeted, try to scroll to it
      if (hash) {
        const id = hash.replace(/^#/, '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView();
          return;
        }
      }

      // Reset window scroll
      window.scrollTo(0, 0);

      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
        document.documentElement.scrollLeft = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
        document.body.scrollLeft = 0;
      }

      // Reset all layout and scrollable containers in the DOM
      const scrollableElements = document.querySelectorAll(
        'main, [data-scroll-container], .overflow-y-auto, .overflow-auto'
      );
      scrollableElements.forEach((el) => {
        el.scrollTop = 0;
        el.scrollLeft = 0;
      });
    };

    // Execute immediately
    resetScroll();

    // Re-verify on next animation frame in case child components mount asynchronously
    const rafId = requestAnimationFrame(resetScroll);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [pathname, search, hash]);

  return null;
}
